import { useState } from 'react';
import { Star, Camera, X, Loader2 } from 'lucide-react';
import { createReview } from '../../lib/api';
import toast from 'react-hot-toast';

function StarRating({ rating, onRate, interactive = false, size = 'h-5 w-5' }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(i)}
          className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
          data-testid={`star-${i}`}
        >
          <Star className={`${size} ${i <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-zinc-700'}`} />
        </button>
      ))}
    </div>
  );
}

const RATING_LABELS = { 1: 'Péssimo', 2: 'Ruim', 3: 'Regular', 4: 'Bom', 5: 'Excelente' };

function ReviewForm({ productId, onSubmit }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(5);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'O campo Nome é obrigatório.';
    if (!email.trim()) errs.email = 'O campo E-mail é obrigatório.';
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) errs.email = 'E-mail inválido.';
    if (!comment.trim()) errs.comment = 'O campo Comentário é obrigatório.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Imagem muito grande (máx 2MB)'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Selecione uma imagem válida'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setPhoto(ev.target.result); setPhotoPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createReview(productId, { name, email, comment, rating, photo });
      toast.success('Avaliação enviada com sucesso!');
      setName(''); setEmail(''); setComment(''); setRating(5); setPhoto(null); setPhotoPreview(null); setErrors({});
      onSubmit();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao enviar avaliação';
      toast.error(msg);
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="review-form">
      <p className="text-zinc-400 text-sm text-center mb-5">Preencha seus dados, avalie e clique no botão Avaliar Produto.</p>

      {/* Nome */}
      <div className="mb-3">
        <input
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setErrors(prev => ({...prev, name: ''})); }}
          placeholder="Nome"
          className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-3 px-4 text-white text-sm placeholder-zinc-500 focus:border-red-600 focus:outline-none"
          data-testid="review-name"
        />
        {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="error-name">{errors.name}</p>}
      </div>

      {/* Email */}
      <div className="mb-3">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({...prev, email: ''})); }}
          placeholder="E-mail"
          className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-3 px-4 text-white text-sm placeholder-zinc-500 focus:border-red-600 focus:outline-none"
          data-testid="review-email"
        />
        {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="error-email">{errors.email}</p>}
      </div>

      {/* Comentário */}
      <div className="mb-4">
        <textarea
          value={comment}
          onChange={(e) => { setComment(e.target.value.slice(0, 500)); setErrors(prev => ({...prev, comment: ''})); }}
          placeholder="Comentários"
          className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-3 px-4 text-white text-sm placeholder-zinc-500 focus:border-red-600 focus:outline-none resize-none"
          rows={4}
          data-testid="review-comment"
        />
        {errors.comment && <p className="text-red-500 text-xs mt-1" data-testid="error-comment">{errors.comment}</p>}
      </div>

      {/* Rating + Photo + Submit */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <StarRating rating={rating} onRate={setRating} interactive size="h-6 w-6" />
          <select
            value={rating}
            onChange={(e) => setRating(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-300 text-sm focus:border-red-600 focus:outline-none"
            data-testid="review-rating-select"
          >
            {[5,4,3,2,1].map(v => <option key={v} value={v}>{RATING_LABELS[v]}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-3">
          {/* Photo upload */}
          <input type="file" accept="image/*" id="review-photo-input" onChange={handlePhoto} className="hidden" data-testid="review-photo-input" />
          <button
            type="button"
            onClick={() => document.getElementById('review-photo-input')?.click()}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-400 hover:text-white px-3 py-2 rounded-lg text-xs transition-colors"
            data-testid="review-add-photo-btn"
          >
            <Camera className="h-4 w-4" />
            {photoPreview ? 'Trocar' : 'Foto'}
          </button>
          {photoPreview && (
            <div className="relative">
              <img src={photoPreview} alt="Preview" className="h-10 w-10 object-cover rounded-lg border border-zinc-700" />
              <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5">
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold py-2.5 px-6 rounded-lg text-sm uppercase tracking-wider transition-colors flex items-center gap-2"
            data-testid="review-submit-btn"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</> : 'Avaliar Produto'}
          </button>
        </div>
      </div>
    </form>
  );
}

function ReviewCard({ review }) {
  const [showFullPhoto, setShowFullPhoto] = useState(false);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="review-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500 text-sm font-bold">
            {(review.user_name || review.reviewer_name || 'C')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white text-sm font-semibold">{review.user_name || review.reviewer_name || 'Cliente'}</p>
            <p className="text-zinc-600 text-[10px]">{new Date(review.created_at).toLocaleDateString('pt-BR')}</p>
          </div>
        </div>
        <StarRating rating={review.rating} size="h-3.5 w-3.5" />
      </div>
      {review.comment && <p className="text-zinc-400 text-sm mb-3 leading-relaxed">{review.comment}</p>}
      {review.photo && (
        <>
          <button onClick={() => setShowFullPhoto(true)} className="block">
            <img src={review.photo} alt="Foto da avaliação" className="h-24 w-24 object-cover rounded-lg border border-zinc-700 hover:border-red-600 transition-colors cursor-pointer" data-testid="review-photo" />
          </button>
          {showFullPhoto && (
            <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowFullPhoto(false)}>
              <button className="absolute top-4 right-4 text-white bg-zinc-800 rounded-full p-2"><X className="h-6 w-6" /></button>
              <img src={review.photo} alt="Foto" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RatingSummary({ avgRating, reviewCount, distribution }) {
  const maxCount = Math.max(...Object.values(distribution || {}), 1);

  return (
    <div className="flex items-start gap-6 bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="rating-summary">
      <div className="text-center min-w-[80px]">
        <p className="text-4xl font-black text-white">{avgRating || '0'}</p>
        <StarRating rating={Math.round(avgRating || 0)} size="h-4 w-4" />
        <p className="text-zinc-500 text-xs mt-1">{reviewCount || 0} avaliações</p>
      </div>
      <div className="flex-1 space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => (
          <div key={star} className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs w-3">{star}</span>
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-500 rounded-full transition-all duration-500" style={{ width: `${((distribution?.[String(star)] || 0) / maxCount) * 100}%` }} />
            </div>
            <span className="text-zinc-600 text-[10px] w-4 text-right">{distribution?.[String(star)] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export { StarRating, ReviewForm, ReviewCard, RatingSummary };
